import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Box, Text, useColorModeValue } from '@chakra-ui/react';

export interface CashFlowMonth {
  month: string; // YYYY-MM
  income: number;
  expenses: number;
}

interface CashFlowChartProps {
  months: CashFlowMonth[];
}

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

const formatMonthLabel = (m: string) => {
  const [year, month] = m.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleString('en-US', { month: 'short', year: '2-digit' });
};

export const CashFlowChart: React.FC<CashFlowChartProps> = ({ months }) => {
  const gridColor = useColorModeValue('#e2e8f0', '#2d3748');
  const tooltipBg = useColorModeValue('#ffffff', '#1a2535');
  const tooltipBorder = useColorModeValue('#e2e8f0', '#2d3748');
  const tooltipText = useColorModeValue('#1a202c', '#e2e8f0');

  if (!months.length) {
    return (
      <Box p={8} textAlign="center" color="gray.500">
        <Text>No data available for the selected period.</Text>
      </Box>
    );
  }

  const data = months.map((m) => ({
    month: formatMonthLabel(m.month),
    Income: m.income,
    Expenses: m.expenses,
  }));

  return (
    <Box w="full" h="340px">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 24, left: 16, bottom: 8 }} barGap={4} barCategoryGap="30%">
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
          <YAxis
            tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
            tick={{ fontSize: 12 }}
            width={60}
          />
          <Tooltip
            formatter={(value, name) => [formatCurrency(Number(value ?? 0)), String(name)]}
            contentStyle={{
              background: tooltipBg,
              border: `1px solid ${tooltipBorder}`,
              borderRadius: '8px',
              color: tooltipText,
            }}
          />
          <Legend />
          <Bar dataKey="Income" fill="#38a169" radius={[4, 4, 0, 0]} />
          <Bar dataKey="Expenses" fill="#e53e3e" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default CashFlowChart;
